// R-131-FIN-DECISION-F — Gantt-style construction → COD → operation → EOL timeline.

import React, { useMemo } from 'react';
import {
  derivePathwayTimeline,
  pathwayMonthsToPower,
} from '../../../lib/finDecision/pathwayCashflows';
import type { Pathway, PathwayPhaseKind } from '../../../lib/finDecision/types';

export interface PathwayLifecycleTimelineProps {
  pathways: Pathway[];
  now?: Date;
}

interface TimelineSegment {
  kind: PathwayPhaseKind;
  startYear: number;
  endYear: number;
  label: string;
}

// Aurora lifecycle phase palette. Returned as CSS background values (not
// Tailwind classes) so each phase fill tracks the theme (flips dark/light)
// instead of a fixed palette stop. Coherent with the cashflow/band charts:
//   construction  -> warning  (amber — pre-revenue build)
//   operation     -> success  (green — revenue period)
//   refurbishment -> chart-3  (violet — mid-life capex)
//   decommissioning -> text-muted (neutral end-of-life)
const SEGMENT_COLOR_VARS: Record<PathwayPhaseKind, string> = {
  construction: 'var(--warning)',
  operation: 'var(--success)',
  refurbishment: 'var(--chart-3)',
  decommissioning: 'var(--text-muted)',
};

function utcYear(isoDate: string): number {
  return new Date(`${isoDate}T00:00:00Z`).getUTCFullYear();
}

function buildSegments(pathway: Pathway): TimelineSegment[] {
  return pathway.phases.map((phase) => {
    const startYear = utcYear(phase.startDate);
    const endYear = startYear + Math.max(1, Math.ceil(phase.durationMonths / 12)) - 1;
    return {
      kind: phase.kind,
      startYear,
      endYear,
      label: phase.kind,
    };
  });
}

/**
 * Format whole months to power as a compact "time to power" chip, e.g.
 * "18 mo" / "1 mo". Shown alongside the COD year so two same-year milestones
 * a few months apart are still distinguishable. Derived from real Dates by
 * `pathwayMonthsToPower` (not years×12).
 */
function formatMonthsToPower(months: number): string {
  return `${months} mo`;
}

export function PathwayLifecycleTimeline({ pathways, now }: PathwayLifecycleTimelineProps) {
  const anchor = now ?? new Date();

  const rows = useMemo(
    () =>
      pathways.map((pathway) => {
        const timeline = derivePathwayTimeline(pathway, anchor);
        return {
          pathway,
          segments: buildSegments(pathway),
          codYear: timeline.codYear,
          firstMwhYear: timeline.codYear,
          // Precise months from the analysis anchor to COD (real Dates).
          monthsToPower: pathwayMonthsToPower(pathway, anchor),
        };
      }),
    [anchor, pathways],
  );

  const { minYear, maxYear } = useMemo(() => {
    let min = anchor.getUTCFullYear();
    let max = min + 1;
    rows.forEach((row) => {
      row.segments.forEach((seg) => {
        min = Math.min(min, seg.startYear);
        max = Math.max(max, seg.endYear);
      });
    });
    return { minYear: min, maxYear: max + 1 };
  }, [anchor, rows]);

  const span = Math.max(1, maxYear - minYear);

  if (pathways.length === 0) return null;

  return (
    <div
      className="rounded-lg border border-border bg-surface-1 p-4"
      data-testid="pathway-lifecycle-timeline"
    >
      <h3 className="text-sm font-semibold text-text-primary mb-3">Lifecycle timeline</h3>
      <div className="space-y-4">
        {rows.map(({ pathway, segments, codYear, monthsToPower }) => (
          <div key={pathway.id}>
            <div className="flex items-start justify-between gap-2 text-xs mb-1">
              {/* Full label — wraps to multiple lines (break-words) instead of
                  truncating with "…", so the whole pathway name is readable. */}
              <span className="font-medium text-text-primary break-words min-w-0">
                {pathway.name}
              </span>
              {/* COD year + precise time-to-power in MONTHS, so two same-year
                  milestones a few months apart stay distinguishable. */}
              <span className="text-text-muted whitespace-nowrap shrink-0">
                First MWh · {codYear} · {formatMonthsToPower(monthsToPower)}
              </span>
            </div>
            <div className="relative h-8 rounded-md border border-border bg-surface-0">
              {segments.map((seg, idx) => {
                const leftPct = ((seg.startYear - minYear) / span) * 100;
                const widthPct = ((seg.endYear - seg.startYear + 1) / span) * 100;
                return (
                  <div
                    key={`${pathway.id}-${seg.kind}-${idx}`}
                    className="absolute top-1 bottom-1 rounded-[2px] opacity-90"
                    style={{
                      left: `${leftPct}%`,
                      width: `${Math.max(widthPct, 1.5)}%`,
                      backgroundColor: SEGMENT_COLOR_VARS[seg.kind],
                    }}
                    title={`${seg.label} ${seg.startYear}–${seg.endYear}`}
                  />
                );
              })}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-primary"
                style={{ left: `${((codYear - minYear) / span) * 100}%` }}
                title={`COD ${codYear}`}
              />
            </div>
            <div className="flex justify-between text-[10px] text-text-muted mt-0.5">
              <span>{minYear}</span>
              <span>{maxYear}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-text-secondary">
        {(Object.keys(SEGMENT_COLOR_VARS) as PathwayPhaseKind[]).map((kind) => (
          <span key={kind} className="inline-flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-[2px]"
              style={{ backgroundColor: SEGMENT_COLOR_VARS[kind] }}
            />
            {kind}
          </span>
        ))}
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-0.5 h-3 bg-primary" />
          COD / first MWh
        </span>
      </div>
    </div>
  );
}
